import { getPathPrefix } from '@/config/runtimeConfig';
import { RankingRelationTypeEnum } from '@/types/RankingRelationTypeEnum';
import { GroupTypeEnum } from '@/types/GroupTypeEnum';

interface ApiUrls {
  [key: string]: string | ((...args: any[]) => string);
}

const apiUrls: ApiUrls = {
  // Configuration
  getConfData: (origin: string) => `${origin}/configuration`,

  // Subjects
  getAllSubjects: () => getPathPrefix() + '/subjects',
  getSubjectById: (subjectId: string) => getPathPrefix() + `/subjects/${subjectId}`,
  addSubject: () => getPathPrefix() + '/subjects',
  editSubject: (subjectId: string) => getPathPrefix() + `/subjects/${subjectId}`,
  importSubject: (subjectId: string) => getPathPrefix() + `/subjects/import/${subjectId}`,
  exportSubject: (subjectId: string) => getPathPrefix() + `/subjects/${subjectId}/export`,

  // Groupable
  getGroupsWithMembers: (
    subjectId: string,
    groupType: GroupTypeEnum,
    rankingRelationUid?: string,
    rankingRelationType?: RankingRelationTypeEnum
  ) => {
    if (rankingRelationUid && rankingRelationType) {
      return (
        getPathPrefix() +
        `/subjects/${subjectId}/${groupType.valueOf().toLowerCase()}-groups/with-members?ranking-relation-type=${rankingRelationType}&ranking-relation-uid=${rankingRelationUid}`
      );
    } else {
      return (
        getPathPrefix() +
        `/subjects/${subjectId}/${groupType.valueOf().toLowerCase()}-groups/with-members`
      );
    }
  },

  // Assets
  getAssetGroups: (subjectId: string) => getPathPrefix() + `/subjects/${subjectId}/asset-groups`,
  getAssetGroupsWithMembers: (subjectId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/asset-groups/with-members`,
  getAssetGroupByUid: (subjectId: string, groupIdentifier: string) =>
    getPathPrefix() + `/subjects/${subjectId}/asset-groups/${groupIdentifier}`,
  addAssetGroup: (subjectId: string) => getPathPrefix() + `/subjects/${subjectId}/asset-groups`,
  editAssetGroupByUid: (subjectId: string, groupIdentifier: string) =>
    getPathPrefix() + `/subjects/${subjectId}/asset-groups/${groupIdentifier}`,
  deleteAssetGroupByUid: (subjectId: string, groupIdentifier: string) =>
    getPathPrefix() + `/subjects/${subjectId}/asset-groups/${groupIdentifier}`,
  getAssetsForGroup: (subjectId: string, groupIdentifier: string, withRisksIds: boolean) =>
    getPathPrefix() +
    `/subjects/${subjectId}/asset-groups/${groupIdentifier}/assets?with-risks-ids=${withRisksIds}`,
  addAssetForGroup: (subjectId: string, groupIdentifier: string) =>
    getPathPrefix() + `/subjects/${subjectId}/asset-groups/${groupIdentifier}/assets`,
  getAssetByUid: (subjectId: string, groupIdentifier: string, memberIdentifier: string) =>
    getPathPrefix() +
    `/subjects/${subjectId}/asset-groups/${groupIdentifier}/assets/${memberIdentifier}`,
  editAssetByUid: (subjectId: string, groupIdentifier: string, memberIdentifier: string) =>
    getPathPrefix() +
    `/subjects/${subjectId}/asset-groups/${groupIdentifier}/assets/${memberIdentifier}`,
  deleteAssetByUid: (subjectId: string, groupIdentifier: string, memberIdentifier: string) =>
    getPathPrefix() +
    `/subjects/${subjectId}/asset-groups/${groupIdentifier}/assets/${memberIdentifier}`,
  cloneAsset: (
    subjectId: string,
    groupIdentifier: string,
    assetIdentifier: string,
    targetGroupIdentifier: string,
    withoutRisks: boolean
  ) =>
    getPathPrefix() +
    `/subjects/${subjectId}/asset-groups/${groupIdentifier}/assets/${assetIdentifier}/clone?target-group=${targetGroupIdentifier}&without-risks=${withoutRisks}`,

  // Threats
  getThreatGroups: (subjectId: string) => getPathPrefix() + `/subjects/${subjectId}/threat-groups`,
  getThreatGroupsWithMembers: (subjectId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/threat-groups/with-members`,
  getThreatGroupByUid: (subjectId: string, groupIdentifier: string) =>
    getPathPrefix() + `/subjects/${subjectId}/threat-groups/${groupIdentifier}`,
  addThreatGroup: (subjectId: string) => getPathPrefix() + `/subjects/${subjectId}/threat-groups`,
  editThreatGroupByUid: (subjectId: string, groupIdentifier: string) =>
    getPathPrefix() + `/subjects/${subjectId}/threat-groups/${groupIdentifier}`,
  deleteThreatGroupByUid: (subjectId: string, groupIdentifier: string) =>
    getPathPrefix() + `/subjects/${subjectId}/threat-groups/${groupIdentifier}`,
  getThreatsForGroup: (subjectId: string, groupIdentifier: string) =>
    getPathPrefix() + `/subjects/${subjectId}/threat-groups/${groupIdentifier}/threats`,
  addThreatForGroup: (subjectId: string, groupIdentifier: string) =>
    getPathPrefix() + `/subjects/${subjectId}/threat-groups/${groupIdentifier}/threats`,
  getThreatByUid: (subjectId: string, groupIdentifier: string, memberIdentifier: string) =>
    getPathPrefix() +
    `/subjects/${subjectId}/threat-groups/${groupIdentifier}/threats/${memberIdentifier}`,
  editThreatByUid: (
    subjectId: string,
    groupIdentifier: string,
    memberIdentifier: string,
    replaceAllProbabilities: boolean
  ) =>
    getPathPrefix() +
    `/subjects/${subjectId}/threat-groups/${groupIdentifier}/threats/${memberIdentifier}?replace-all-probability=${replaceAllProbabilities}`,
  deleteThreatByUid: (subjectId: string, groupIdentifier: string, memberIdentifier: string) =>
    getPathPrefix() +
    `/subjects/${subjectId}/threat-groups/${groupIdentifier}/threats/${memberIdentifier}`,

  // Threat sources
  getAllThreatSourceTypes: (subjectId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/threat-source-types`,
  addThreatSourceType: (subjectId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/threat-source-types`,
  getThreatSourceType: (subjectId: string, identifier: string) =>
    getPathPrefix() + `/subjects/${subjectId}/threat-source-types/${identifier}`,
  editThreatSourceType: (subjectId: string, identifier: string) =>
    getPathPrefix() + `/subjects/${subjectId}/threat-source-types/${identifier}`,
  deleteThreatSourceType: (subjectId: string, identifier: string) =>
    getPathPrefix() + `/subjects/${subjectId}/threat-source-types/${identifier}`,

  // Threat source types
  getAllThreatSources: (subjectId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/threat-sources`,
  addThreatSource: (subjectId: string) => getPathPrefix() + `/subjects/${subjectId}/threat-sources`,
  getThreatSource: (subjectId: string, identifier: string) =>
    getPathPrefix() + `/subjects/${subjectId}/threat-sources/${identifier}`,
  editThreatSource: (subjectId: string, identifier: string) =>
    getPathPrefix() + `/subjects/${subjectId}/threat-sources/${identifier}`,
  deleteThreatSource: (subjectId: string, identifier: string) =>
    getPathPrefix() + `/subjects/${subjectId}/threat-sources/${identifier}`,

  // Vulnerabilities
  getVulnerabilityGroups: (subjectId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/vulnerability-groups`,
  getVulnerabilityGroupsWithMembers: (subjectId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/vulnerability-groups/with-members`,
  getVulnerabilityGroupByUid: (subjectId: string, groupIdentifier: string) =>
    getPathPrefix() + `/subjects/${subjectId}/vulnerability-groups/${groupIdentifier}`,
  addVulnerabilityGroup: (subjectId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/vulnerability-groups`,
  editVulnerabilityGroupByUid: (subjectId: string, groupIdentifier: string) =>
    getPathPrefix() + `/subjects/${subjectId}/vulnerability-groups/${groupIdentifier}`,
  deleteVulnerabilityGroupByUid: (subjectId: string, groupIdentifier: string) =>
    getPathPrefix() + `/subjects/${subjectId}/vulnerability-groups/${groupIdentifier}`,
  getVulnerabilitiesForGroup: (subjectId: string, groupIdentifier: string) =>
    getPathPrefix() +
    `/subjects/${subjectId}/vulnerability-groups/${groupIdentifier}/vulnerabilities`,
  addVulnerabilityForGroup: (subjectId: string, groupIdentifier: string) =>
    getPathPrefix() +
    `/subjects/${subjectId}/vulnerability-groups/${groupIdentifier}/vulnerabilities`,
  getVulnerabilityByUid: (subjectId: string, groupIdentifier: string, memberIdentifier: string) =>
    getPathPrefix() +
    `/subjects/${subjectId}/vulnerability-groups/${groupIdentifier}/vulnerabilities/${memberIdentifier}`,
  editVulnerabilityByUid: (subjectId: string, groupIdentifier: string, memberIdentifier: string) =>
    getPathPrefix() +
    `/subjects/${subjectId}/vulnerability-groups/${groupIdentifier}/vulnerabilities/${memberIdentifier}`,
  deleteVulnerabilityByUid: (
    subjectId: string,
    groupIdentifier: string,
    memberIdentifier: string
  ) =>
    getPathPrefix() +
    `/subjects/${subjectId}/vulnerability-groups/${groupIdentifier}/vulnerabilities/${memberIdentifier}`,

  // Measures
  getMeasureGroups: (subjectId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/measure-groups`,
  getMeasureGroupsWithMembers: (subjectId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/measure-groups/with-members`,
  getMeasureGroupByUid: (subjectId: string, groupIdentifier: string) =>
    getPathPrefix() + `/subjects/${subjectId}/measure-groups/${groupIdentifier}`,
  addMeasureGroup: (subjectId: string) => getPathPrefix() + `/subjects/${subjectId}/measure-groups`,
  editMeasureGroupByUid: (subjectId: string, groupIdentifier: string) =>
    getPathPrefix() + `/subjects/${subjectId}/measure-groups/${groupIdentifier}`,
  deleteMeasureGroupByUid: (subjectId: string, groupIdentifier: string) =>
    getPathPrefix() + `/subjects/${subjectId}/measure-groups/${groupIdentifier}`,
  getMeasuresForGroup: (subjectId: string, groupIdentifier: string) =>
    getPathPrefix() + `/subjects/${subjectId}/measure-groups/${groupIdentifier}/measures`,
  addMeasureForGroup: (subjectId: string, groupIdentifier: string) =>
    getPathPrefix() + `/subjects/${subjectId}/measure-groups/${groupIdentifier}/measures`,
  getMeasureByUid: (subjectId: string, groupIdentifier: string, memberIdentifier: string) =>
    getPathPrefix() +
    `/subjects/${subjectId}/measure-groups/${groupIdentifier}/measures/${memberIdentifier}`,
  editMeasureByUid: (subjectId: string, groupIdentifier: string, memberIdentifier: string) =>
    getPathPrefix() +
    `/subjects/${subjectId}/measure-groups/${groupIdentifier}/measures/${memberIdentifier}`,
  deleteMeasureByUid: (subjectId: string, groupIdentifier: string, memberIdentifier: string) =>
    getPathPrefix() +
    `/subjects/${subjectId}/measure-groups/${groupIdentifier}/measures/${memberIdentifier}`,

  // Implementations
  getAllImplementations: (subjectId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/implemented-measures`,
  getAllImplementationsForMeasure: (subjectId: string, measureUid: string) =>
    getPathPrefix() + `/subjects/${subjectId}/implemented-measures?measure-uid=${measureUid}`,
  addImplementation: (subjectId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/implemented-measures`,
  getImplementation: (subjectId: string, identifier: string) =>
    getPathPrefix() + `/subjects/${subjectId}/implemented-measures/${identifier}`,
  editImplementation: (subjectId: string, identifier: string) =>
    getPathPrefix() + `/subjects/${subjectId}/implemented-measures/${identifier}`,
  deleteImplementation: (subjectId: string, identifier: string) =>
    getPathPrefix() + `/subjects/${subjectId}/implemented-measures/${identifier}`,

  // Users
  getAllUsers: (subjectId: string) => getPathPrefix() + `/subjects/${subjectId}/users`,
  addUser: (subjectId: string) => getPathPrefix() + `/subjects/${subjectId}/users`,
  getUser: (subjectId: string, userId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/users/${userId}`,
  editUser: (subjectId: string, userId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/users/${userId}`,
  deleteUser: (subjectId: string, userId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/users/${userId}`,

  // Confidentiality levels
  getAllConfidentialityLevels: (subjectId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/confidentiality-levels`,
  addConfidentialityLevel: (subjectId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/confidentiality-levels`,
  getConfidentialityLevel: (subjectId: string, confidentialityLevelId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/confidentiality-levels/${confidentialityLevelId}`,
  editConfidentialityLevel: (subjectId: string, confidentialityLevelId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/confidentiality-levels/${confidentialityLevelId}`,
  deleteConfidentialityLevel: (subjectId: string, confidentialityLevelId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/confidentiality-levels/${confidentialityLevelId}`,

  // Document references
  getAllDocumentReferences: (subjectId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/document-references`,
  addDocumentReference: (subjectId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/document-references`,
  getDocumentReference: (subjectId: string, docRefId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/document-references/${docRefId}`,
  editDocumentReference: (subjectId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/document-references`,
  deleteDocumentReference: (subjectId: string, docRefId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/document-references/${docRefId}`,

  // Impacts
  getAllImpacts: (subjectId: string) => getPathPrefix() + `/subjects/${subjectId}/strategic-axises`,
  addImpact: (subjectId: string) => getPathPrefix() + `/subjects/${subjectId}/strategic-axises`,
  getImpact: (subjectId: string, identifier: string) =>
    getPathPrefix() + `/subjects/${subjectId}/strategic-axises/${identifier}`,
  editImpact: (subjectId: string, identifier: string) =>
    getPathPrefix() + `/subjects/${subjectId}/strategic-axises/${identifier}`,
  deleteImpact: (subjectId: string, identifier: string) =>
    getPathPrefix() + `/subjects/${subjectId}/strategic-axises/${identifier}`,

  // Import - Export
  importFromFile: (subjectId: string, type: string) =>
    getPathPrefix() + `/subjects/${subjectId}/import/${type}`,

  // Licences
  getAllLicences: () => getPathPrefix() + `/licences/`,
  getLicenceBySubject: (subjectId: string) => getPathPrefix() + `/licences/subjects/${subjectId}`,
  createDeveloperLicenceRequest: () => getPathPrefix() + `/licences/dev-request`,
  createSubjectLicenceRequest: () => getPathPrefix() + `/licences/subject-request`,
  createMasterLicenceRequest: () => getPathPrefix() + `/licences/ml-request`,
  uploadLicenceFile: () => getPathPrefix() + `/licences/upload-licence`,

  // Risk acceptance decisions
  getAllRiskAcceptanceDecisions: (subjectId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/risk-acceptance-decisions`,
  addRiskAcceptanceDecision: (subjectId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/risk-acceptance-decisions`,
  getRiskAcceptanceDecision: (subjectId: string, identifier: string) =>
    getPathPrefix() + `/subjects/${subjectId}/risk-acceptance-decisions/${identifier}`,
  editRiskAcceptanceDecision: (subjectId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/risk-acceptance-decisions`,
  deleteRiskAcceptanceDecision: (subjectId: string, identifier: string) =>
    getPathPrefix() + `/subjects/${subjectId}/risk-acceptance-decisions/${identifier}`,

  // Asset vulnerability connection
  getAvConnections: (subjectId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/asset-vulnerabilities`,
  setAvConnections: (subjectId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/asset-vulnerabilities`,
  getAvConnectionsForAsset: (subjectId: string, assetId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/asset-vulnerabilities/asset/${assetId}`,

  // Risks
  getAllRisks: (subjectId: string) => getPathPrefix() + `/subjects/${subjectId}/risks`,
  getRisksForAv: (subjectId: string, avId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/risks/by-asset-vulnerability-id/${avId}`,
  getRisksForAsset: (subjectId: string, aId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/risks/by-asset-id/${aId}`,
  getRiskByUid: (subjectId: string, riskUid: string) =>
    getPathPrefix() + `/subjects/${subjectId}/risks/${riskUid}`,
  addRisk: (subjectId: string) => getPathPrefix() + `/subjects/${subjectId}/risks`,
  editRisk: (subjectId: string) => getPathPrefix() + `/subjects/${subjectId}/risks`,
  deleteRisk: (subjectId: string, riskUid: string) =>
    getPathPrefix() + `/subjects/${subjectId}/risks/${riskUid}`,
  getRiskIndex: (subjectId: string, riskUid: string) =>
    getPathPrefix() + `/subjects/${subjectId}/risks/index/${riskUid}`,
  getDynamicsRiskIndex: (subjectId: string) =>
    getPathPrefix() + `/subjects/${subjectId}/risks/index`,

  // Search
  searchSingleObject: (
    subjectId: string,
    objectTypeUid: string,
    searchedObjectType: string,
    uid: string
  ) =>
    getPathPrefix() +
    `/subjects/${subjectId}/search-single-object?uid=${uid}&object-type-of-uid=${objectTypeUid}&searched-object-type=${searchedObjectType}`,

  // Statistics
  getAllAssetGroupsStatistics: (subjectId: string) =>
    getPathPrefix() + `/statistics/${subjectId}/asset-groups`,
  getAssetGroupStatistics: (subjectId: string, agIdentifier: string) =>
    getPathPrefix() + `/statistics/${subjectId}/asset-groups/${agIdentifier}`,
  getAssetGroupStatisticsWithRcFilter: (
    subjectId: string,
    agIdentifier: string,
    rcFilter: string
  ) =>
    getPathPrefix() +
    `/statistics/${subjectId}/asset-groups/${agIdentifier}?risk-category=${rcFilter}`,
  getAllMeasureGroupsStatistics: (subjectId: string) =>
    getPathPrefix() + `/statistics/${subjectId}/measure-groups`,
  getMeasureGroupStatistics: (subjectId: string, mgIdentifier: string) =>
    getPathPrefix() + `/statistics/${subjectId}/measure-groups/${mgIdentifier}`,
  getMeasureGroupWithImplementationStatistics: (subjectId: string, mgIdentifier: string) =>
    getPathPrefix() + `/statistics/${subjectId}/measure-groups/${mgIdentifier}/with-implementation`,
  getMeasureImplementationCountByStatusStatistics: (subjectId: string) =>
    getPathPrefix() + `/statistics/${subjectId}/io-measure-count-by-status`,
  getCountsByOwnersStatistics: (subjectId: string) =>
    getPathPrefix() + `/statistics/${subjectId}/counts-by-owners`,

  // Diag
  getDiagConfig: () => getPathPrefix() + '/diag/config',
  getDiagInfo: () => getPathPrefix() + '/diag/info',

  // Reports
  getManagerialReport: (subjectId: string, confLevelUid: string, createDate: string) =>
    getPathPrefix() +
    `/reports/${subjectId}/managerial-report/?conf-level-uid=${confLevelUid}&created-date=${createDate}`,
  getAssetsReport: (subjectId: string, confLevelUid: string, createDate: string) => {
    const url = new URL(getPathPrefix() + `/reports/${subjectId}/assets`, 'http://localhost');
    if (confLevelUid) url.searchParams.append('conf-level-uid', confLevelUid);
    if (createDate) url.searchParams.append('created-date', createDate);
    return url.toString().replace('http://localhost', '');
  },
  getThreatsReport: (subjectId: string, confLevelUid: string, createDate: string) => {
    const url = new URL(getPathPrefix() + `/reports/${subjectId}/threats`, 'http://localhost');
    if (confLevelUid) url.searchParams.append('conf-level-uid', confLevelUid);
    if (createDate) url.searchParams.append('created-date', createDate);
    return url.toString().replace('http://localhost', '');
  },
  getVulnerabilitiesReport: (subjectId: string, confLevelUid: string, createDate: string) => {
    const url = new URL(
      getPathPrefix() + `/reports/${subjectId}/vulnerabilities`,
      'http://localhost'
    );
    if (confLevelUid) url.searchParams.append('conf-level-uid', confLevelUid);
    if (createDate) url.searchParams.append('created-date', createDate);
    return url.toString().replace('http://localhost', '');
  },
  getMeasuresReport: (subjectId: string, confLevelUid: string, createDate: string) => {
    const url = new URL(getPathPrefix() + `/reports/${subjectId}/measures`, 'http://localhost');
    if (confLevelUid) url.searchParams.append('conf-level-uid', confLevelUid);
    if (createDate) url.searchParams.append('created-date', createDate);
    return url.toString().replace('http://localhost', '');
  },
  getImpactReport: (subjectId: string, confLevelUid: string, createDate: string) => {
    const url = new URL(
      getPathPrefix() + `/reports/${subjectId}/strategic-axises`,
      'http://localhost'
    );
    if (confLevelUid) url.searchParams.append('conf-level-uid', confLevelUid);
    if (createDate) url.searchParams.append('created-date', createDate);
    return url.toString().replace('http://localhost', '');
  },
  getRiskByThreadsReport: (
    subjectId: string,
    confLevelUid: string,
    createDate: string,
    riskMinLevel: string
  ) => {
    const url = new URL(
      getPathPrefix() + `/reports/${subjectId}/risk-by-threats`,
      'http://localhost'
    );
    if (confLevelUid) {
      url.searchParams.append('conf-level-uid', confLevelUid);
    }
    if (createDate) {
      url.searchParams.append('created-date', createDate);
    }
    if (riskMinLevel) {
      url.searchParams.append('risk-min-level', riskMinLevel);
    }
    return url.toString().replace('http://localhost', '');
  },
  getRiskByAssetsReport: (
    subjectId: string,
    confLevelUid: string,
    createDate: string,
    riskMinLevel: string,
    filterAssetGroupUid: string,
    filterAssetUid: string
  ) => {
    const url = new URL(
      getPathPrefix() + `/reports/${subjectId}/risk-by-assets`,
      'http://localhost'
    );

    if (confLevelUid) {
      url.searchParams.append('conf-level-uid', confLevelUid);
    }
    if (createDate) {
      url.searchParams.append('created-date', createDate);
    }
    if (riskMinLevel) {
      url.searchParams.append('risk-min-level', riskMinLevel);
    }
    if (filterAssetGroupUid) {
      url.searchParams.append('filter-asset-group-uid', filterAssetGroupUid);
    }
    if (filterAssetUid) {
      url.searchParams.append('filter-asset-uid', filterAssetUid);
    }

    return url.toString().replace('http://localhost', '');
  },

  // Tags
  getAllTags: (subjectId: string) => getPathPrefix() + `/subjects/${subjectId}/tags`,
  addTag: (subjectId: string) => getPathPrefix() + `/subjects/${subjectId}/tags`,
  getTagByUid: (subjectId: string) => getPathPrefix() + `/subjects/${subjectId}/tags/by-uids`,
  getTag: (subjectId: string, tagUid: string) =>
    getPathPrefix() + `/subjects/${subjectId}/tags/${tagUid}/tag-uid`,
  editTag: (subjectId: string, tagUid: string) =>
    getPathPrefix() + `/subjects/${subjectId}/tags/${tagUid}/tag-uid`,
  deleteTag: (subjectId: string, tagUid: string) =>
    getPathPrefix() + `/subjects/${subjectId}/tags/${tagUid}/tag-uid`,

  // Edit token
  requestEditToken: (subjectId: string) => getPathPrefix() + `/subjects/${subjectId}/edit-tokens`,
  getEditToken: (subjectId: string) => getPathPrefix() + `/subjects/${subjectId}/edit-tokens`,
  freeEditToken: (subjectId: string) => getPathPrefix() + `/subjects/${subjectId}/edit-tokens`,
  prolongEditToken: (subjectId: string) => getPathPrefix() + `/subjects/${subjectId}/edit-tokens`,

  riskTreatment: (subjectId: string, assetGroupId?: string, referenceDate?: string) => {
    const url = new URL(
      getPathPrefix() + `/subjects/${subjectId}/risk-treatment/plan-xls`,
      'http://localhost'
    );
    if (assetGroupId) url.searchParams.append('asset-group-id', assetGroupId);
    if (referenceDate) url.searchParams.append('reference-date', referenceDate);
    return url.toString().replace('http://localhost', '');
  },
};

export default apiUrls;
